import { TOURNAMENT } from "@/data/tournament";
import { useTeams } from "@/hooks/useTeams";

const HERO_IMG =
  "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodCUyMHNwb3RsaWdodHN8ZW58MHx8fHwxNzgwODQyMjQ4fDA&ixlib=rb-4.1.0&q=85";

const Hero = () => {
  const teams = useTeams();
  const availablePlazas = TOURNAMENT.totalTeams - teams.length;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Totem Sports Center - estadio nocturno"
          className="w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
      </div>

      {/* Grain */}
      <div className="absolute inset-0 grain pointer-events-none" />

      {/* Top ticker */}
      <div className="relative z-10 pt-20 border-b border-white/10">
        <div className="overflow-hidden">
          <div className="marquee whitespace-nowrap flex gap-12 py-3 text-xs tracking-[0.3em] uppercase text-white/40">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="flex items-center gap-12">
                <span>● Edición I · 2026</span>
                <span>● Girona</span>
                <span>● Fútbol 7</span>
                <span>● {TOURNAMENT.dates}</span>
                <span>● Premio 1.000€</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-20 sm:pt-28 pb-16">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-8" data-testid="hero-label">
              <div className="w-12 h-px bg-[#EAB308]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#EAB308] font-medium">
                {TOURNAMENT.edition} · {TOURNAMENT.city}
              </span>
            </div>

            <h1
              className="font-display text-[18vw] sm:text-[14vw] lg:text-[10.5rem] leading-[0.85] tracking-tight uppercase text-white"
              data-testid="hero-title"
            >
              16 Equipos.
              <br />
              <span className="text-[#EAB308] gold-text-glow">1 Campeón.</span>
            </h1>

            <p className="mt-8 text-white/70 text-base sm:text-lg max-w-2xl leading-relaxed font-body">
              La nueva competición de fútbol 7 amateur. Fase de liga,
              eliminatoria final, árbitros en todos los partidos y seguro
              incluido. Todo en
              una sola jornada: el 25 de octubre en Totem Sports Center
              (Tordera).
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="#inscripciones"
                data-testid="hero-inscribir-btn"
                className="group inline-flex items-center justify-center gap-3 bg-[#EAB308] text-black px-8 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:bg-[#facc15] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(234,179,8,0.35)]"
              >
                Inscribir Equipo
                <span className="text-lg leading-none group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </a>
              <a
                href="#torneo"
                data-testid="hero-info-btn"
                className="inline-flex items-center justify-center gap-3 border border-white/30 text-white px-8 py-4 text-sm tracking-[0.2em] uppercase font-bold hover:border-white hover:bg-white/5 transition-all"
              >
                Ver Información
              </a>
            </div>
          </div>

          {/* Plazas counter */}
          <div className="lg:col-span-4">
            <div
              data-testid="plazas-counter"
              className="relative border border-white/15 bg-[#0a0a0a]/80 backdrop-blur-sm p-8 gold-glow"
            >
              <div className="absolute top-0 left-0 w-4 h-px bg-[#EAB308]" />
              <div className="absolute top-0 left-0 h-4 w-px bg-[#EAB308]" />
              <div className="absolute bottom-0 right-0 w-4 h-px bg-[#EAB308]" />
              <div className="absolute bottom-0 right-0 h-4 w-px bg-[#EAB308]" />

              <div className="flex items-center gap-2 mb-6">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-white/60">
                  Inscripciones · Apertura {TOURNAMENT.registrationOpens}
                </span>
              </div>

              <p className="text-xs tracking-[0.25em] uppercase text-white/50 mb-2">
                Plazas Disponibles
              </p>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-7xl sm:text-8xl text-white leading-none">
                  {String(availablePlazas).padStart(2, "0")}
                </span>
                <span className="text-white/40 text-xl">
                  / {TOURNAMENT.totalTeams}
                </span>
              </div>

              <div className="mt-6 h-1 w-full bg-white/10 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#EAB308]"
                  style={{
                    width: `${(availablePlazas / TOURNAMENT.totalTeams) * 100}%`,
                  }}
                />
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-xs tracking-[0.15em] uppercase">
                <div>
                  <div className="text-white/40 mb-1">Confirmados</div>
                  <div className="text-white font-bold">
                    {teams.length} equipos
                  </div>
                </div>
                <div>
                  <div className="text-white/40 mb-1">Inscripción</div>
                  <div className="text-[#EAB308] font-bold">{TOURNAMENT.price}€</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
